import React, { FC, ChangeEvent, useMemo } from 'react';
import { Field, useFormikContext } from 'formik';
import { Accordion, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircleFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import { matchType, filterClassFormatValues } from '../../../../../constant/play';
import { ClassFormatEnum, BracketOptions, ClassTypeEnum } from '../../../../../types/enums';
import { BracketCellGroup } from '../../../../../utils/groupMatchesByBracketCell';
import {
  parseBracketCellLayout,
  syncBracketCellLayoutToMatches,
} from '../../../../../utils/bracketCellLayoutSync.util';
import { traslateItemWithLabel } from '../../../../../utils/traslation.helpers';
import type { ClassValues } from '../../../TournamentClasses/components/ClassesTag/ClassesTag';
import { MatchesLabelItem } from '../MatchesLabelItem';
import { ShowDontKnowTeamOrError } from '../ShowDontKnowTeamOrError';
import { TeamPlayCard } from '../TeamPlayCard';
import type { PlayValues } from '../PlayTag/PlayTag';
import { convertToBooleanString } from '../Match/Match.helpers';

type BracketCellSettingsAccordionProps = {
  group: BracketCellGroup;
  currentClass: ClassValues | undefined;
  variant?: 'group' | 'elimination';
  showCellLevelTeams?: boolean;
  handleAddTeamForBracketCell?: (bracketCellId: string, fieldName: string) => void;
};

export const BracketCellSettingsAccordion: FC<BracketCellSettingsAccordionProps> = ({
  group,
  currentClass,
  variant = 'elimination',
  showCellLevelTeams,
  handleAddTeamForBracketCell,
}) => {
  const { t } = useTranslation();
  const formik = useFormikContext<PlayValues>();
  const { values, setFieldValue } = formik;
  const prefix = 'creativeDetail-';
  const buttonPrefix = 'button-';
  const primary = group.matches[0];
  const cellField = `bracketCells.${group.cellId}`;
  const cellForm = values.bracketCells[group.cellId];
  const formMode = cellForm?.progressionMode;
  const effectiveProgressionMode =
    formMode !== undefined && formMode !== ''
      ? Number(formMode)
      : primary.bracketCellProgressionMode;

  const effectiveRound =
    cellForm?.round !== undefined && cellForm.round !== ''
      ? Number(cellForm.round)
      : Number(primary.type ?? 1);
  const effectiveIsGold =
    cellForm?.isGold !== undefined
      ? convertToBooleanString(cellForm.isGold)
      : convertToBooleanString(primary.isGold);
  const effectiveBracket =
    cellForm?.bracket !== undefined && cellForm.bracket !== ''
      ? Number(cellForm.bracket)
      : Number(primary.bracket ?? 0);

  const roundOptions = useMemo(
    () =>
      (variant === 'group'
        ? matchType.filter((field) => !filterClassFormatValues.includes(field.value))
        : matchType
      ).map(({ label, value }) => ({
        label: t(label, { roundNumber: value }),
        value,
      })),
    [t, variant],
  );

  const showBracketControl =
    variant === 'elimination' &&
    !primary.tournamentGroupId &&
    (currentClass?.format === ClassFormatEnum.BRACKET ||
      currentClass?.format === ClassFormatEnum.GROUP_BRACKET);

  const applyLayoutSync = (layout: {
    round?: number | string;
    isGold?: boolean | string;
    bracket?: number | string;
  }) => {
    syncBracketCellLayoutToMatches(
      formik,
      group.cellId,
      parseBracketCellLayout({
        round: layout.round ?? effectiveRound,
        isGold: layout.isGold ?? effectiveIsGold,
        bracket: layout.bracket ?? effectiveBracket,
      }),
    );
  };

  const handleRoundChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const round = Number(e.target.value);
    setFieldValue(`${cellField}.round`, round);
    applyLayoutSync({ round });
  };

  const handleIsGoldChange = (value: string) => {
    setFieldValue(`${cellField}.isGold`, value);
    applyLayoutSync({ isGold: value });
  };

  const handleBracketChange = (value: number) => {
    setFieldValue(`${cellField}.bracket`, value);
    applyLayoutSync({ bracket: value });
  };

  const isTeamsClass = currentClass?.type === ClassTypeEnum.TEAMS;
  const showManualWinnerUi = isTeamsClass
    ? true
    : currentClass?.autoProgress === false || Number(effectiveProgressionMode) === 2;

  const homeId = primary.homeTeam?.id;
  const awayId = primary.awayTeam?.id;

  const handleWinnerChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const winnerId = e.target.value;
    setFieldValue(`${cellField}.winnerTeamId`, winnerId);
    if (winnerId === '') {
      setFieldValue(`${cellField}.loserTeamId`, '');
      return;
    }
    const winnerNum = Number(winnerId);
    const loserId =
      winnerNum === homeId ? awayId : winnerNum === awayId ? homeId : '';
    setFieldValue(`${cellField}.loserTeamId`, loserId ?? '');
  };

  const winnerTeamId = cellForm?.winnerTeamId;
  const derivedLoserName =
    winnerTeamId !== '' && winnerTeamId != null
      ? Number(winnerTeamId) === homeId
        ? primary.awayTeam?.name
        : Number(winnerTeamId) === awayId
          ? primary.homeTeam?.name
          : undefined
      : undefined;

  const showTieBreakHint = primary.bracketCellProgressionMode === 0;

  const progressionExplanation = primary.bracketCellProgressionExplanation;
  const decidingRuleType =
    progressionExplanation &&
    typeof progressionExplanation === 'object' &&
    progressionExplanation.pairResolution &&
    typeof progressionExplanation.pairResolution === 'object'
      ? (progressionExplanation.pairResolution as { decidingRuleType?: string }).decidingRuleType
      : undefined;

  const optionHome =
    primary.homeTeam?.id != null ? (
      <option value={primary.homeTeam.id}>{primary.homeTeam.name}</option>
    ) : null;
  const optionAway =
    primary.awayTeam?.id != null ? (
      <option value={primary.awayTeam.id}>{primary.awayTeam.name}</option>
    ) : null;

  return (
    <Accordion defaultActiveKey="" className="mb-2">
      <Accordion.Item eventKey="settings">
        <Accordion.Header>{t(prefix + 'bracketCellSettingsTitle')}</Accordion.Header>
        <Accordion.Body className="py-2">
          {showCellLevelTeams && handleAddTeamForBracketCell ? (
            <>
            <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
              <div className="d-flex align-items-center gap-2">
                <MatchesLabelItem
                  btnCb={() => handleAddTeamForBracketCell(group.cellId, 'homeTeam')}
                  btnTitle={t(buttonPrefix + 'addChangeTeam')}
                  label={t(prefix + 'homeTeam')}
                />
                {primary.homeTeam ? (
                  <TeamPlayCard team={primary.homeTeam} />
                ) : (
                  <ShowDontKnowTeamOrError
                    match={primary}
                    placeholderKey="homeTeamPlaceholder"
                  />
                )}
              </div>
              <div className="d-flex align-items-center gap-2">
                <MatchesLabelItem
                  btnCb={() => handleAddTeamForBracketCell(group.cellId, 'awayTeam')}
                  btnTitle={t(buttonPrefix + 'addChangeTeam')}
                  label={t(prefix + 'awayTeam')}
                />
                {primary.awayTeam ? (
                  <TeamPlayCard team={primary.awayTeam} />
                ) : (
                  <ShowDontKnowTeamOrError
                    match={primary}
                    placeholderKey="awayTeamPlaceholder"
                  />
                )}
              </div>
            </div>
            <p className="small text-muted mb-3">{t(prefix + 'bracketCellHomeAwayHint')}</p>
            </>
          ) : null}
          {primary.bracketCellStatus === 1 && currentClass?.autoProgress !== false ? (
            <div className="small text-warning mb-2">
              {t(prefix + 'bracketCellAutoProgressPending')}
            </div>
          ) : null}
          {showTieBreakHint ? (
            <div className="small mb-2 d-flex align-items-center gap-1">
              <span>{t(prefix + 'bracketCellTieBreakHint')}</span>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`tie-settings-${group.cellId}`}>
                    {t(prefix + 'bracketCellTieBreakTooltip')}
                  </Tooltip>
                }
              >
                <InfoCircleFill className="text-info" size={14} aria-hidden />
              </OverlayTrigger>
            </div>
          ) : null}
          {progressionExplanation ? (
            <div className="small text-muted mb-2">
              <strong>{t(prefix + 'bracketCellProgressionExplanation')}:</strong>
              {decidingRuleType ? (
                <span className="ms-1">
                  {t(prefix + 'bracketCellDecidingRule', { rule: decidingRuleType })}
                </span>
              ) : null}
              {progressionExplanation.kind === 'TIEBREAK_REQUIRES_MANUAL' ? (
                <div className="text-warning mt-1">
                  {t(prefix + 'bracketCellProgressionManualRequired')}
                </div>
              ) : null}
              {progressionExplanation.kind === 'DRAWN_MEETING' ? (
                <div className="text-muted mt-1">
                  {t(prefix + 'bracketCellDrawnMeeting')}
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="row g-2 mb-2">
            <label className="col-12 col-md-3 col-form-label small">
              {t(prefix + 'bracketCellRound')}
            </label>
            <div className="col-12 col-md-4">
              <select
                className="form-select form-select-sm"
                value={effectiveRound}
                onChange={handleRoundChange}
              >
                {roundOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {effectiveRound === 13 ? (
            <div className="row g-2 mb-2">
              <label className="col-12 col-md-3 col-form-label small">
                {t(prefix + 'type')}
              </label>
              <div className="col-12 col-md-4">
                <div className="border py-1 d-flex flex-row tagContainer">
                  {(['true', 'false'] as const).map((value) => (
                    <div
                      key={value}
                      className={`d-flex flex-grow-1 justify-content-center align-items-center py-1 mx-1 btnTabItem ${
                        effectiveIsGold === value ? 'activeBtn' : ''
                      }`}
                      role="button"
                      onClick={() => handleIsGoldChange(value)}
                    >
                      {value === 'true' ? t('toggle-gold') : t('toggle-bronze')}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
          {showBracketControl ? (
            <div className="row g-2 mb-2">
              <label className="col-12 col-md-3 col-form-label small">
                {t(prefix + 'bracket')}
              </label>
              <div className="col-12 col-md-6">
                <div className="border py-1 d-flex flex-row tagContainer">
                  {BracketOptions.map((item) => {
                    const label = traslateItemWithLabel(t, item);
                    return (
                      <div
                        key={item.value}
                        className={`d-flex flex-grow-1 justify-content-center align-items-center py-1 mx-1 btnTabItem ${
                          effectiveBracket === item.value ? 'activeBtn' : ''
                        }`}
                        role="button"
                        onClick={() => handleBracketChange(item.value)}
                      >
                        {label.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}
          <div className="row g-2 mb-2">
            <label className="col-12 col-md-3 col-form-label small">
              {t(prefix + 'bracketCellStatus')}
            </label>
            <div className="col-12 col-md-4">
              <Field as="select" name={`${cellField}.status`} className="form-select form-select-sm">
                <option value={0}>{t(prefix + 'bracketCellStatusUpcoming')}</option>
                <option value={1}>{t(prefix + 'bracketCellStatusInProgress')}</option>
                <option value={2}>{t(prefix + 'bracketCellStatusCompleted')}</option>
                <option value={3}>{t(prefix + 'bracketCellStatusCancelled')}</option>
              </Field>
              <div className="small text-muted mt-1">{t(prefix + 'bracketCellStatusHint')}</div>
            </div>
          </div>
          {showManualWinnerUi ? (
            <div className="row g-2 mb-2">
              <label className="col-12 col-md-3 col-form-label small">
                {t(prefix + 'bracketCellManualWinner')}
              </label>
              <div className="col-12 col-md-4">
                <Field
                  as="select"
                  name={`${cellField}.winnerTeamId`}
                  className="form-select form-select-sm"
                  data-testid="bracket-cell-manual-winner"
                  onChange={handleWinnerChange}
                >
                  <option value="">{t(prefix + 'bracketCellManualWinnerPlaceholder')}</option>
                  {optionHome}
                  {optionAway}
                </Field>
                {derivedLoserName ? (
                  <div className="small text-muted mt-1">{derivedLoserName}</div>
                ) : null}
              </div>
            </div>
          ) : null}
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};
